import { CheckOutlined, CloseOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Carousel, Modal } from 'antd';
import React, { useRef, useState } from 'react';
import MsgV2Slide1 from '~/assets/onboarding/msg-v2-slide-1.png';
import MsgV2Slide2 from '~/assets/onboarding/msg-v2-slide-2.png';
import MsgV2Slide3 from '~/assets/onboarding/msg-v2-slide-3.png';
import MsgV2Slide4 from '~/assets/onboarding/msg-v2-slide-4.png';
import { getOnBoardingVersion, setOnBoardingVersion } from '~/helpers/cookies';

import './OnBoarding.scss';

// Bump this version to show onboarding to all users again
const ONBOARDING_VERSION = 2;

function CheckItem({ children }) {
  return (
    <div className='checkmark-text-block'>
      <CheckOutlined style={{ color: '#e5007e', marginTop: 4, flexShrink: 0 }} />
      <span className='body1'>{children}</span>
    </div>
  );
}

const onboardingSteps = [
  {
    img: (
      <div className='img-step-block' key='img-step-0'>
        <img src={MsgV2Slide1} alt='step-0' className='w-[250px] md:w-[300px]' />
      </div>
    ),
    description: (
      <div className='desc-step-block' key='desc-step-0'>
        <h3 className='h3'>La messagerie évolue</h3>
        <p className='body1'>L'amélioration de votre outil de travail continue. Venez découvrir les nouveautés de votre messagerie Labo.</p>
      </div>
    ),
  },
  {
    img: (
      <div className='img-step-block' key='img-step-1'>
        <img src={MsgV2Slide2} alt='step-1' className='w-[300px] md:w-[400px]' />
      </div>
    ),
    description: (
      <div className='desc-step-block' key='desc-step-1'>
        <h3 className='h3'>Partagez des notes en interne</h3>
        <CheckItem>Laissez une note privée à votre équipe dans une conversation</CheckItem>
        <CheckItem>Communiquez entre vous pour clôturer une réclamation plus rapidement</CheckItem>
      </div>
    ),
  },
  {
    img: (
      <div className='img-step-block' key='img-step-2'>
        <img src={MsgV2Slide3} alt='step-2' className='w-[300px] md:w-[420px]' />
      </div>
    ),
    description: (
      <div className='desc-step-block' key='desc-step-2'>
        <h3 className='h3'>Plus de confort</h3>
        <CheckItem>Plus d'espace pour vos messages dans le fil de la conversation</CheckItem>
        <CheckItem>Retrouvez tout de suite les messages qui attendent votre réponse</CheckItem>
      </div>
    ),
  },
  {
    img: (
      <div className='img-step-block' key='img-step-3'>
        <img src={MsgV2Slide4} alt='step-3' className='w-[300px] md:w-[420px]' />
      </div>
    ),
    description: (
      <div className='desc-step-block' key='desc-step-3'>
        <h3 className='h3'>Répondez en un claquement de doigts</h3>
        <CheckItem>
          Utilisez les <b>modèles de réponses</b> rédigés pour chaque motif de réclamation
        </CheckItem>
        <CheckItem>Personnalisez les modèles</CheckItem>
      </div>
    ),
  },
];

function Steps({ stepIndex, totalSteps }) {
  const stepsIcons = [];
  for (let x = 0; x < totalSteps; x++) {
    stepsIcons.push(<div key={x} className={`dot-icon${x === stepIndex ? ' current-dot' : ''}`} />);
  }
  return <div className='dot-block'>{stepsIcons}</div>;
}

function OnBoardingNavigation({ totalSteps, refCarousels, finishOnBoarding }) {
  const [stepIndex, setStepIndex] = useState(0);
  const lastStepIndex = totalSteps - 1;

  const goNextSlide = () => {
    if (stepIndex < lastStepIndex) {
      refCarousels.forEach((ref) => {
        ref.current.next();
      });
      setStepIndex(stepIndex + 1);
    }
  };

  const goPreviousSlide = () => {
    if (stepIndex > 0) {
      refCarousels.forEach((ref) => {
        ref.current.prev();
      });
      setStepIndex(stepIndex - 1);
    }
  };

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === lastStepIndex;

  return (
    <div className='navigation-flex-container'>
      {!isFirstStep && (
        <Button className='skip-onboarding-btn' type='text' onClick={finishOnBoarding}>
          Passer
        </Button>
      )}
      {!isFirstStep && <Button onClick={goPreviousSlide}>Précédent</Button>}
      <Steps stepIndex={stepIndex} totalSteps={totalSteps} />
      {isLastStep ? (
        <Button type='primary' onClick={finishOnBoarding}>
          J'ai compris
        </Button>
      ) : (
        <Button type='primary' onClick={goNextSlide}>
          {isFirstStep ? "C'est parti !" : 'Suivant'} {!isFirstStep && <RightOutlined />}
        </Button>
      )}
    </div>
  );
}

function OnBoardingModalContent({ finishOnBoarding }) {
  const refCarouselImgs = useRef();
  const refCarouselDescription = useRef();

  return (
    <div className='onboarding-block'>
      <div className='close-icon-onboarding-wrapper'>
        <CloseOutlined className='close-icon-onboarding' onClick={finishOnBoarding} />
      </div>
      <div className='onboarding-left-block'>
        <Carousel ref={refCarouselImgs} dots={null} draggable={false} touchMove={false} accessibility={false}>
          {onboardingSteps.map((step) => step.img)}
        </Carousel>
      </div>
      <div className='onboarding-right-block'>
        <div className='onboarding-carousel-desc-block'>
          <Carousel ref={refCarouselDescription} dots={null} draggable={false} touchMove={false} accessibility={false}>
            {onboardingSteps.map((step) => step.description)}
          </Carousel>
        </div>
        <div className='onboarding-navigation-block'>
          <OnBoardingNavigation
            totalSteps={onboardingSteps.length}
            refCarousels={[refCarouselImgs, refCarouselDescription]}
            finishOnBoarding={finishOnBoarding}
          />
        </div>
      </div>
    </div>
  );
}

export default function OnBoarding() {
  const savedVersion = getOnBoardingVersion();
  const shouldShow = savedVersion === null || savedVersion < ONBOARDING_VERSION;
  const [isModalOpen, setIsModalOpen] = useState(shouldShow);

  const finishOnBoarding = () => {
    setIsModalOpen(false);
    setOnBoardingVersion(ONBOARDING_VERSION);
  };

  if (!isModalOpen) {
    return null;
  }

  return (
    <Modal
      className='onboarding-modal'
      zIndex={2000}
      width={950}
      styles={{ mask: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      open={isModalOpen}
      closeIcon={null}
      footer={null}
      maskClosable={false}
      destroyOnClose
      centered
    >
      <OnBoardingModalContent finishOnBoarding={finishOnBoarding} />
    </Modal>
  );
}
